import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Drawer } from 'vaul';
import { Theme } from '@radix-ui/themes';
import { ResponsiveModalProps } from './ResponsiveModal';
import AppContainer from './ui/AppContainer';
import { safeWindow } from '@frontend/utils/safeWindow';

export const BottomSheetModal = ({
  children,
  trigger,
  onOpenChange,
  open: controlledOpen,
  showClose = true,
}: ResponsiveModalProps) => {
  // Use internal state if not controlled externally
  const [internalOpen, setInternalOpen] = useState(false);
  const [pointerStart, setPointerStart] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Determine if we're using a controlled or uncontrolled mode
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  // Handle pointer down to track start position
  const handlePointerDown = (e: React.PointerEvent) => {
    setPointerStart({ x: e.clientX, y: e.clientY });
  };

  // Handle trigger click - only if it wasn't a swipe
  const handleTriggerClick = (e: React.PointerEvent) => {
    if (!pointerStart) return;

    const deltaX = Math.abs(e.clientX - pointerStart.x);
    const deltaY = Math.abs(e.clientY - pointerStart.y);
    const threshold = 10; // pixels

    // If movement is too large, it's a swipe, not a click
    if (deltaX > threshold || deltaY > threshold) {
      setPointerStart(null);
      return;
    }

    e.stopPropagation(); // Prevent event bubbling

    if (isControlled) {
      onOpenChange?.(true);
    } else {
      setInternalOpen(true);
    }

    setPointerStart(null);
  };

  // Handle open change from Drawer component
  const handleOpenChange = (newOpen: boolean) => {
    // If showClose is false and trying to close, prevent closing
    if (!showClose && !newOpen) {
      return;
    }

    if (isControlled) {
      onOpenChange?.(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  const drawerContent = (
    <Drawer.Root
      open={open}
      onOpenChange={handleOpenChange}
      dismissible={showClose}
      modal={true}
    >
      <Drawer.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity duration-300 z-[499]" />
      <Drawer.Content className="flex flex-col rounded-t-[16px] mt-12 fixed bottom-0 left-0 right-0 z-[9999] shadow-lg transition-transform duration-300 bg-[var(--gray-1)] max-h-[85vh] border-t-4 border-t-gray-400/30">
        <div className="flex justify-center py-3">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>

        <div className="flex-1 overflow-y-auto">
          <AppContainer mt="2" className="pb-6">
            {children}
          </AppContainer>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );

  return (
    <>
      {trigger && (
        <div
          onPointerDown={open ? undefined : handlePointerDown}
          onPointerUp={open ? undefined : handleTriggerClick}
        >
          {trigger}
        </div>
      )}

      {typeof safeWindow !== 'undefined' ? (
        createPortal(<Theme>{drawerContent}</Theme>, document.body)
      ) : (
        <Theme>{drawerContent}</Theme>
      )}
    </>
  );
};
